import { type Config } from 'tailwindcss'

export const extendedTheme = {
	colors: {
		border: 'hsl(var(--border))',
		input: {
			DEFAULT: 'hsl(var(--input))',
			invalid: 'hsl(var(--input-invalid))',
		},
		'dark-start': '#2A2A2A',
		'dark-end': '#000000',
		'dark-start-hover': '#2E2E2E',
		'dark-end-hover': '#242424',
		'dark-start-focus': '#121212',
		'dark-end-focus': '#1A1A1A',
		'light-start': '#FFFFFF',
		'light-end': '#FAFAFA',
		'light-start-hover': '#FFFFFF',
		'light-end-hover': '#F5F5F5',
		'light-start-focus': '#F2F2F2',
		'light-end-focus': '#F2F2F2',
		ring: {
			DEFAULT: 'hsl(var(--ring))',
			invalid: 'hsl(var(--foreground-destructive))',
		},
		background: 'hsl(var(--background))',
		foreground: {
			DEFAULT: 'hsl(var(--foreground))',
			destructive: 'hsl(var(--foreground-destructive))',
		},
		primary: {
			DEFAULT: 'hsl(var(--primary))',
			foreground: 'hsl(var(--primary-foreground))',
		},
		secondary: {
			DEFAULT: 'hsl(var(--secondary))',
			foreground: 'hsl(var(--secondary-foreground))',
		},
		destructive: {
			DEFAULT: 'hsl(var(--destructive))',
			foreground: 'hsl(var(--destructive-foreground))',
		},
		muted: {
			DEFAULT: 'hsl(var(--muted))',
			foreground: 'hsl(var(--muted-foreground))',
		},
		accent: {
			DEFAULT: 'hsl(var(--accent))',
			foreground: 'hsl(var(--accent-foreground))',
		},
		popover: {
			DEFAULT: 'hsl(var(--popover))',
			foreground: 'hsl(var(--popover-foreground))',
		},
		card: {
			DEFAULT: 'hsl(var(--card))',
			foreground: 'hsl(var(--card-foreground))',
		},
		label: '#7C7C7C',
	},
	borderColor: {
		DEFAULT: 'hsl(var(--border))',
	},
	borderRadius: {
		lg: 'var(--radius)',
		md: 'calc(var(--radius) - 2px)',
		sm: 'calc(var(--radius) - 4px)',
	},
	boxShadow: {
		DEFAULT: 'var(--shadow)',
		button: '0px 1px 2px rgba(0, 0, 0, 0.15)',
	},
	fontFamily: {
		sans: ['var(--font-body)'],
		headers: ['var(--font-headers)'],
	},
	fontSize: {
		// 1rem = 16px
		/** 80px size / 84px high / bold */
		mega: ['60px', { lineHeight: '1.4em', fontWeight: '500' }],
		/** 56px size / 62px high / bold */
		h1: ['32px', { lineHeight: '1.4em', fontWeight: '500' }],
		/** 40px size / 48px high / bold */
		h2: ['24px', { lineHeight: '1.4em', fontWeight: '500' }],
		/** 32px size / 36px high / bold */
		h3: ['20px', { lineHeight: '1.4em', fontWeight: '500' }],
		/** 28px size / 36px high / bold */
		h4: ['20px', { lineHeight: '1.4em', fontWeight: '500' }],
		/** 24px size / 32px high / bold */
		h5: ['1.5rem', { lineHeight: '1.4em', fontWeight: '500' }],
		/** 16px size / 20px high / bold */
		h6: ['20px', { lineHeight: '1.25rem', fontWeight: '500' }],

		/** 32px size / 36px high / normal */
		'body-2xl': ['2rem', { lineHeight: '2.25rem' }],
		/** 28px size / 36px high / normal */
		'body-xl': ['1.75rem', { lineHeight: '2.25rem' }],
		/** 24px size / 32px high / normal */
		'body-lg': ['1.5rem', { lineHeight: '2rem' }],
		/** 20px size / 28px high / normal */
		'body-md': ['1.25rem', { lineHeight: '1.75rem' }],
		/** 16px size / 20px high / normal */
		'body-sm': ['14px', { lineHeight: '22.4px' }],
		/** 14px size / 18px high / normal */
		'body-xs': ['0.875rem', { lineHeight: '1.125rem' }],
		/** 12px size / 16px high / normal */
		'body-2xs': ['0.75rem', { lineHeight: '1rem' }],

		/** 18px size / 24px high / semibold */
		caption: ['1.125rem', { lineHeight: '1.5rem', fontWeight: '600' }],
		/** 12px size / 16px high / bold */
		button: ['0.75rem', { lineHeight: '1rem', fontWeight: '700' }],
	},
	keyframes: {
		'caret-blink': {
			'0%,70%,100%': { opacity: '1' },
			'20%,50%': { opacity: '0' },
		},
		shake: {
			'0%, 100%': { transform: 'translateX(0)' },
			'10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-10px)' },
			'20%, 40%, 60%, 80%': { transform: 'translateX(10px)' },
		},
		'spin-slow': {
			'0%': { transform: 'rotate(0deg)' },
			'100%': { transform: 'rotate(360deg)' },
		},
		slide: {
			'0%': {
				left: '-50%',
			},
			'50%': {
				left: '100%',
			},
			'100%': {
				left: '-50%',
			},
		},
		contentShow: {
			from: {
				opacity: '0',
				transform: 'translate(-50%, -48%) scale(0.96)',
			},
			to: {
				opacity: '1',
				transform: 'translate(-50%, -50%) scale(1)',
			},
		},
		overlayShow: {
			from: {
				opacity: '0',
			},
			to: {
				opacity: '1',
			},
		},
	},
	animation: {
		shake: 'shake 0.5s',
		slide: 'slide 1s infinite',
		'spin-slow': 'spin-slow 10s linear infinite',
		showModal: 'contentShow 300ms cubic-bezier(0.16, 1, 0.3, 1)',
		showModalOverlay: 'overlayShow 300ms cubic-bezier(0.16, 1, 0.3, 1)',
		'caret-blink': 'caret-blink 1.25s ease-out infinite',
	},
} satisfies Config['theme']
